<template>
    <div class="asset-versions">
        <div class="d-flex flex-wrap align-items-center mb-3">
            <b-form-select
                v-model="assetTypeFilter"
                :options="assetTypeFilterOptions"
                style="max-width: 220px"
                class="mr-2"
                aria-label="자산 필터"
                @change="onFilterChange"
            />
            <span class="text-muted mr-auto">
                <template v-if="!loadError">총 {{ assets.length }}개 버전</template>
            </span>
            <b-button variant="outline-secondary" @click="$emit('check-gate')">
                골든 게이트 확인
            </b-button>
        </div>

        <!-- DS 표 규칙: 행 44 고정 — 칸마다 한 줄, 넘치면 줄이고 전체는 title. 폭은 머리가 정한다 -->
        <b-table
            :items="assets"
            :fields="fields"
            :busy="loading"
            table-class="ds-table ds-table-fixed asset-version-table"
            hover
            responsive
            show-empty
        >
            <template #empty>
                <PageEmptyComponent
                    compact
                    :error="loadError ? '버전 목록을 불러오지 못했습니다.' : ''"
                    :text="assetTypeFilter ? '이 자산의 저장된 버전이 없습니다.' : '저장된 버전이 없습니다. 피드백 인박스에서 첫 버전을 만들어 주세요.'"
                    @retry="fetchAssets"
                />
            </template>

            <!-- 자산 이름 뒤에 코드를 흐린 글자로 -->
            <template #cell(asset_type)="row">
                <span class="ds-ellipsis" :title="`${assetTypeLabel(row.value)} (${row.value})`">{{ assetTypeLabel(row.value) }} <span class="ds-sub">{{ row.value }}</span></span>
            </template>

            <!-- 메인 칸 = 내용 보기 창. 기준 버전은 '← v' 로 뒤에 -->
            <template #cell(version)="row">
                <span class="ds-ellipsis" :title="row.item.base_version ? `v${row.value} · 기준 v${row.item.base_version}` : `v${row.value}`"><button type="button" class="ds-detail-link" @click="openContent(row.item)">v{{ row.value }}</button> <span v-if="row.item.base_version" class="ds-sub">← v{{ row.item.base_version }}</span></span>
            </template>

            <template #cell(is_active)="row">
                <span v-if="row.value" class="ds-status ds-status-success">활성</span>
                <span v-else class="ds-sub">-</span>
            </template>

            <!-- 메모는 생성 당시 "무엇이 달라지나요" 요약(2~4문장) — 한 줄로 줄이고 전문은 title -->
            <template #cell(note)="row">
                <span class="ds-ellipsis" :title="row.value || ''">{{ row.value || '-' }}</span>
            </template>

            <template #cell(created_at)="row">
                {{ formatDateTime(row.value) }}
            </template>

            <!-- 행 동작(아이콘 28): 활성화(비활성 행만 · 빈 자리 유지) · 내용 보기(버전 링크와 같은 동작) -->
            <template #cell(actions)="row">
                <div class="d-flex justify-content-end" style="gap: 4px">
                    <button
                        v-if="!row.item.is_active"
                        type="button"
                        class="ds-row-icon-btn"
                        :disabled="activatingId === row.item.id"
                        :aria-label="`${assetTypeLabel(row.item.asset_type)} v${row.item.version} 활성화`"
                        :title="`v${row.item.version} 활성화`"
                        @click="activate(row.item)"
                    >
                        <AppIcon name="circle-check"/>
                    </button>
                    <span v-else class="ds-row-icon-btn" aria-hidden="true"></span>
                    <button
                        type="button"
                        class="ds-row-icon-btn"
                        :aria-label="`${assetTypeLabel(row.item.asset_type)} v${row.item.version} 내용 보기`"
                        title="내용 보기"
                        @click="openContent(row.item)"
                    >
                        <AppIcon name="chevron-right"/>
                    </button>
                </div>
            </template>
        </b-table>

        <!-- 콘텐츠 전문 -->
        <b-modal
            id="asset-content-modal"
            size="xl"
            scrollable
            ok-only
            ok-title="닫기"
            :title="contentTitle"
        >
            <div v-if="contentLoading" class="text-center text-muted py-4">
                불러오는 중입니다.
            </div>
            <pre v-else class="asset-content">{{ contentAsset && contentAsset.content }}</pre>
        </b-modal>
    </div>
</template>

<script>
import { getErrorMessage } from '@core/utils/api/errorMessage';
import PageEmptyComponent from '@shared/components/admin/components/common/PageEmptyComponent.vue';
import {
    ASSET_TYPE_OPTIONS,
    assetTypeLabel,
    firstQueryValue,
    formatDateTime,
    sameQuery,
} from './prompt-labels';

export default {
    name: 'AssetVersionsTab',
    components: {PageEmptyComponent},
    data() {
        return {
            assets: [],
            loading: false,
            loadError: false,
            fetchReq: 0,
            assetTypeFilter: '',
            activatingId: null,
            contentAsset: null,
            contentLoading: false,
            assetTypeFilterOptions: [
                {value: '', text: '전체 자산'},
                ...ASSET_TYPE_OPTIONS,
            ],
            // 폭은 머리가 정한다(ds-table-fixed) — 폭을 안 적은 '무엇이 달라졌나' 가 남는 폭을 갖는다
            fields: [
                {key: 'asset_type', label: '자산', thStyle: {width: '180px'}},
                {key: 'version', label: '버전', thStyle: {width: '100px'}},
                {key: 'is_active', label: '상태', thStyle: {width: '80px'}},
                {key: 'note', label: '무엇이 달라졌나'},
                {key: 'created_at', label: '저장', tdClass: 'ds-cell-nowrap', thStyle: {width: '140px'}},
                // 아이콘 2개 28 x 2 + 간격 4 + 좌우 16
                {key: 'actions', label: '동작', thClass: 'text-right', tdClass: 'text-right', thStyle: {width: '92px'}},
            ],
        };
    },
    computed: {
        contentTitle() {
            if (!this.contentAsset) return '버전 내용';
            return `${assetTypeLabel(this.contentAsset.asset_type)} v${this.contentAsset.version}`;
        },
    },
    watch: {
        // 뒤로가기 등으로 같은 화면의 URL 이 바뀌면 필터를 맞춘다(화면이 직접 바꾼 URL 은 이미 같은 상태).
        '$route.query'(query) {
            this.applyQuery(query);
        },
    },
    created() {
        this.applyQuery(this.$route.query, true);
    },
    methods: {
        assetTypeLabel,
        formatDateTime,
        // ?asset_type= 은 자산 종류 목록에 있는 값만 받는다. 전체(빈 값)는 생략.
        applyQuery(query, force = false) {
            const value = firstQueryValue(query.asset_type);
            const assetType = ASSET_TYPE_OPTIONS.some((o) => o.value === value) ? value : '';
            if (!force && assetType === this.assetTypeFilter) return;
            this.assetTypeFilter = assetType;
            this.fetchAssets();
        },
        onFilterChange() {
            const query = {...this.$route.query};
            delete query.asset_type;
            if (this.assetTypeFilter) query.asset_type = this.assetTypeFilter;
            if (!sameQuery(query, this.$route.query)) this.$router.push({query}).catch(() => {});
            this.fetchAssets();
        },
        async fetchAssets() {
            const req = ++this.fetchReq;  // 필터를 연달아 바꿨을 때 늦게 온 이전 응답은 버린다
            this.loading = true;
            this.loadError = false;
            try {
                const params = this.assetTypeFilter ? {asset_type: this.assetTypeFilter} : {};
                const {data} = await this.$axios.get('/supplement-prompt/assets', {params});
                if (req !== this.fetchReq) return;
                this.assets = (data && data.assets) || [];
            } catch (e) {
                if (req !== this.fetchReq) return;
                // 지난 목록으로 활성화하지 않도록 비우고 '없음' 대신 오류를 보인다.
                this.assets = [];
                this.loadError = true;
                if (e?.response?.status === 401) return;
                this.$bvToast.toast(getErrorMessage(e, '버전 목록을 불러오지 못했습니다.'), {
                    title: '오류', variant: 'danger',
                });
            } finally {
                if (req === this.fetchReq) this.loading = false;
            }
        },
        async openContent(asset) {
            // 목록 응답에는 content 가 빠져 있어 단건 조회로 전문을 받아온다.
            this.contentAsset = {asset_type: asset.asset_type, version: asset.version, content: ''};
            this.contentLoading = true;
            this.$bvModal.show('asset-content-modal');
            try {
                const {data} = await this.$axios.get(`/supplement-prompt/assets/${asset.id}`);
                this.contentAsset = data;
            } catch (e) {
                this.$bvModal.hide('asset-content-modal');
                this.$bvToast.toast(getErrorMessage(e, '버전 내용을 불러오지 못했습니다.'), {
                    title: '오류', variant: 'danger',
                });
            } finally {
                this.contentLoading = false;
            }
        },
        async activate(asset) {
            const ok = await this.$bvModal.msgBoxConfirm(
                `${assetTypeLabel(asset.asset_type)} v${asset.version} 을 활성본으로 전환합니다. `
                + '활성화 후 60초 내 판정 파이프라인에 반영됩니다. '
                + '활성화 전 수렴 지표 탭에서 골든 게이트를 확인하는 것을 권장합니다.',
                {title: '활성화 확인', okTitle: '활성화', cancelTitle: '취소'},
            );
            if (!ok) return;

            this.activatingId = asset.id;
            try {
                await this.$axios.post(`/supplement-prompt/assets/${asset.id}/activate`);
                this.$bvToast.toast(
                    `${assetTypeLabel(asset.asset_type)} v${asset.version} 을 활성화했습니다.`,
                    {title: '완료', variant: 'success'},
                );
                await this.fetchAssets();
            } catch (e) {
                // 저장 당시 통과했어도 활성화 시점에 검증 규칙이 강화됐을 수 있다 (서버 400).
                this.$bvToast.toast(getErrorMessage(e, '활성화에 실패했습니다.'), {
                    title: '오류', variant: 'danger',
                });
            } finally {
                this.activatingId = null;
            }
        },
    },
};
</script>

<style scoped>
/* 고정 열 합 592(자산 180 · 버전 100 · 상태 80 · 저장 140 · 동작 92) + 무엇이 달라졌나 ~248.
   1366 화면 · 좌측 메뉴 펼침의 탭 본문 폭(약 1032 = 1074 - 카드 테두리 2 - 탭 여백 40) 안 — 더 좁으면 가로 스크롤 */
.asset-versions ::v-deep .asset-version-table {
    min-width: 840px;
}

.asset-content {
    white-space: pre-wrap;
    word-break: break-word;
    font-family: Consolas, Monaco, 'Courier New', monospace;
    font-size: 0.85rem;
    margin-bottom: 0;
}
</style>
